import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Shield, FileCheck, Users, BarChart, Clock, Award, ChevronRight, Loader2, AlertCircle, Brain, Zap, Lock } from 'lucide-react';
import SiteLayout from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { useCMSContent } from '../hooks/useCMSContent';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

// Icon mapping for dynamic icon rendering from CMS
const iconMap = {
  Shield, FileCheck, Users, BarChart, Clock, Award, Brain, Zap, Lock
};

const LandingPage = () => {
  const { content: cmsContent, loading } = useCMSContent('site-content');
  const [livePlans, setLivePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Fetch live plans from Platform Pricing document (same source as PricingPage)
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        const pricingRef = doc(db, 'platform', 'pricing');
        const pricingSnap = await getDoc(pricingRef);

        if (pricingSnap.exists() && pricingSnap.data().packages) {
          const fetchedPlans = pricingSnap.data().packages;
          // Sort by sortOrder if available
          fetchedPlans.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
          setLivePlans(fetchedPlans);
        }
      } catch (err) {
        console.error('Failed to fetch plans for landing page:', err);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Format price for display
  const formatPrice = (plan) => {
    const price = plan.price || 0;
    return `$${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Use CMS content only - no fallbacks
  const hero = cmsContent?.hero;
  const stats = cmsContent?.stats;
  const features = cmsContent?.features;
  const howItWorks = cmsContent?.howItWorks;
  const benefits = cmsContent?.benefits;
  const pricing = cmsContent?.pricing;
  const cta = cmsContent?.cta;

  // Show error if required CMS content is not configured
  if (!hero) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Landing page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/site-content</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero Section */}
      {hero && (
        <section className="relative py-20 md:py-32 bg-gradient-to-br from-primary/5 via-background to-secondary/5 overflow-hidden">
          <div className="container-wide relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {hero.badge && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                  <Shield size={16} />
                  {hero.badge}
                </div>
              )}
              {(hero.title || hero.titleHighlight) && (
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  {hero.title}
                  {hero.titleHighlight && <span className="text-primary"> {hero.titleHighlight}</span>}
                </h1>
              )}
              {hero.subtitle && (
                <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  {hero.subtitle}
                </p>
              )}
              {(hero.ctaPrimary || hero.ctaSecondary) && (
                <div className="flex flex-wrap gap-4 justify-center mb-12">
                  {hero.ctaPrimary && hero.ctaPrimaryHref && (
                    <Link to={hero.ctaPrimaryHref}>
                      <Button size="xl" className="gap-2">
                        {hero.ctaPrimary}
                        <ArrowRight size={18} />
                      </Button>
                    </Link>
                  )}
                  {hero.ctaSecondary && hero.ctaSecondaryHref && (
                    <Link to={hero.ctaSecondaryHref}>
                      <Button size="xl" variant="outline">
                        {hero.ctaSecondary}
                      </Button>
                    </Link>
                  )}
                </div>
              )}

              {/* Hero Image */}
              {hero.heroImage && (
                <div className="relative mx-auto max-w-5xl rounded-xl overflow-hidden shadow-2xl border border-border/50">
                  <img
                    src={hero.heroImage}
                    alt={hero.heroImageAlt || ''}
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent pointer-events-none"></div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Stats Section */}
      {stats && stats.length > 0 && (
        <section className="py-16 bg-card border-y border-border">
          <div className="container-wide">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {stat.label}
                  </div>
                  {stat.description && (
                    <div className="text-xs text-muted-foreground/70 mt-1">
                      {stat.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {features && (
        <section id="features" className="py-20 md:py-32">
          <div className="container-wide">
            {(features.sectionTitle || features.sectionSubtitle) && (
              <div className="text-center mb-16">
                {features.sectionTitle && (
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {features.sectionTitle}
                  </h2>
                )}
                {features.sectionSubtitle && (
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    {features.sectionSubtitle}
                  </p>
                )}
              </div>
            )}

            {features.items && features.items.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.items.map((feature, index) => {
                  const Icon = iconMap[feature.icon] || Shield;
                  return (
                    <div key={index} className="p-6 bg-card border border-border rounded-lg hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                      {feature.features && (
                        <ul className="mt-4 space-y-2">
                          {feature.features.map((f, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Check size={14} className="text-primary flex-shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* How It Works */}
      {howItWorks && (
        <section className="py-20 md:py-32 bg-muted/50">
          <div className="container-wide">
            {(howItWorks.sectionTitle || howItWorks.sectionSubtitle) && (
              <div className="text-center mb-16">
                {howItWorks.sectionTitle && (
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {howItWorks.sectionTitle}
                  </h2>
                )}
                {howItWorks.sectionSubtitle && (
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    {howItWorks.sectionSubtitle}
                  </p>
                )}
              </div>
            )}

            {howItWorks.steps && howItWorks.steps.length > 0 && (
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
                {howItWorks.steps.map((item, index) => (
                  <div key={index} className="relative">
                    <div className="text-6xl font-bold text-primary/10 mb-4">{item.step}</div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                    {item.details && (
                      <ul className="mt-4 space-y-1">
                        {item.details.map((detail, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Check size={12} className="text-primary flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Benefits / Why ISOcore */}
      {benefits && (
        <section className="py-20 md:py-32">
          <div className="container-wide">
            {(benefits.sectionTitle || benefits.sectionSubtitle) && (
              <div className="text-center mb-16">
                {benefits.sectionTitle && (
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {benefits.sectionTitle}
                  </h2>
                )}
                {benefits.sectionSubtitle && (
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    {benefits.sectionSubtitle}
                  </p>
                )}
              </div>
            )}

            {benefits.items && benefits.items.length > 0 && (
              <div className="grid md:grid-cols-3 gap-8">
                {benefits.items.map((benefit, index) => (
                  <div key={index} className="bg-card border border-border rounded-lg p-8 flex flex-col h-full hover:shadow-md transition-all">
                    <div className="flex-1 mb-6">
                      <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                      <p className="text-muted-foreground">
                        {benefit.description}
                      </p>
                    </div>
                    {(benefit.category || benefit.badge) && (
                      <div className="flex items-center gap-4 pt-6 border-t border-border/50">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Shield size={20} className="text-primary" />
                        </div>
                        <div>
                          {benefit.category && <div className="font-semibold text-sm">{benefit.category}</div>}
                          {benefit.badge && <div className="text-xs text-muted-foreground">{benefit.badge}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Pricing Preview - Uses live plans from platform/pricing */}
      {pricing && (
        <section className="py-20 md:py-32 bg-muted/50">
          <div className="container-wide">
            {(pricing.sectionTitle || pricing.sectionSubtitle) && (
              <div className="text-center mb-16">
                {pricing.sectionTitle && (
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    {pricing.sectionTitle}
                  </h2>
                )}
                {pricing.sectionSubtitle && (
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                    {pricing.sectionSubtitle}
                  </p>
                )}
              </div>
            )}

            {/* Show loading state while fetching plans */}
            {plansLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}

            {/* Live pricing plans from platform/pricing document */}
            {!plansLoading && livePlans.length > 0 && (
              <div className={`grid gap-8 max-w-5xl mx-auto ${
                livePlans.length === 3 ? 'md:grid-cols-3' :
                livePlans.length === 2 ? 'md:grid-cols-2 max-w-4xl' :
                'md:grid-cols-1 max-w-lg'
              }`}>
                {livePlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative bg-card border rounded-xl p-8 ${plan.isPopular ? 'border-primary shadow-lg' : 'border-border'}`}
                  >
                    {plan.isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {pricing.popularLabel || 'Most Popular'}
                      </div>
                    )}
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <div className="mb-6">
                      <span className="text-3xl font-bold">{formatPrice(plan)}</span>
                      <span className="text-muted-foreground">/mo</span>
                    </div>
                    {plan.features && plan.features.length > 0 && (
                      <ul className="space-y-3 mb-8">
                        {plan.features.slice(0, 5).map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Check size={16} className="text-primary" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link to={`/signup?plan=${plan.id}`}>
                      <Button
                        variant={plan.isPopular ? 'default' : 'outline'}
                        className="w-full"
                      >
                        Get Started
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}

            {pricing.viewAllLink && pricing.viewAllHref && (
              <div className="text-center mt-8">
                <Link to={pricing.viewAllHref} className="text-primary hover:underline inline-flex items-center gap-1">
                  {pricing.viewAllLink}
                  <ChevronRight size={16} />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      {cta && (
        <section className="py-20 md:py-32 bg-primary text-white">
          <div className="container-wide text-center">
            {cta.title && (
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {cta.title}
              </h2>
            )}
            {cta.subtitle && (
              <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                {cta.subtitle}
              </p>
            )}
            {(cta.primaryButton || cta.secondaryButton) && (
              <div className="flex flex-wrap gap-4 justify-center">
                {cta.primaryButton && cta.primaryHref && (
                  <Link to={cta.primaryHref}>
                    <Button size="xl" className="bg-white text-primary hover:bg-white/90 gap-2">
                      {cta.primaryButton}
                      <ArrowRight size={18} />
                    </Button>
                  </Link>
                )}
                {cta.secondaryButton && cta.secondaryHref && (
                  <Link to={cta.secondaryHref}>
                    <Button size="xl" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                      {cta.secondaryButton}
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </SiteLayout>
  );
};

export default LandingPage;
